import { apiClient } from '@/config/api';
import { API_BASE_URL } from '@/config/apiBaseUrl';

export interface ProjectDocument {
  id: number;
  filename: string;
  originalName: string;
  filePath: string;
  mimeType: string;
  size: number;
  uploadedBy: string;
  createdAt: string;
}

export const projectDocumentService = {
  // Get all documents for a project
  async getDocuments(projectId: number): Promise<ProjectDocument[]> {
    const response = await apiClient.get<ProjectDocument[]>(`/projects/${projectId}/documents`);
    return response.data;
  },

  // Upload a document
  async uploadDocument(projectId: number, file: File): Promise<ProjectDocument> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ProjectDocument>(`/projects/${projectId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Download a document
  async downloadDocument(projectId: number, documentId: number): Promise<Blob> {
    try {
      const response = await apiClient.get(`/projects/${projectId}/documents/${documentId}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: Blob | { error?: string }; status?: number } };
      const data = axiosError.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const json = JSON.parse(text) as { error?: string; message?: string };
          throw new Error(json.error || json.message || 'Erreur lors du téléchargement du document');
        } catch (e) {
          if (e instanceof Error && e.message !== 'Erreur lors du téléchargement du document') throw e;
          throw new Error(text?.trim() || 'Erreur lors du téléchargement du document');
        }
      }
      if (typeof data === 'object' && data !== null && 'error' in data) {
        throw new Error((data as { error?: string }).error || 'Erreur lors du téléchargement du document');
      }
      throw err;
    }
  },

  // Delete a document
  async deleteDocument(projectId: number, documentId: number): Promise<void> {
    await apiClient.delete(`/projects/${projectId}/documents/${documentId}`);
  },

  // Get download URL (for direct link)
  getDownloadUrl(projectId: number, documentId: number): string {
    return `${API_BASE_URL}/projects/${projectId}/documents/${documentId}`;
  },
};
